import React, { useState, useRef, useEffect } from 'react';
import { IoMdClose } from 'react-icons/io';
import { IoChatbubbleEllipsesOutline } from 'react-icons/io5';
import { FaSpinner } from 'react-icons/fa';
import { RiSendPlaneFill } from 'react-icons/ri';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from "remark-gfm";

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const messagesEndRef = useRef(null);
  const userId = useRef(`user_${Math.random().toString(36).substr(2, 9)}`);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !hasInitialized) {
      sendWelcomeMessage();
      setHasInitialized(true);
    }
  }, [isOpen, hasInitialized]);

  const sendWelcomeMessage = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('https://ai-hairstyle-studio-1-9ldn.onrender.com/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          user_id: userId.current,
          message: 'hi',
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setMessages([{ role: 'assistant', text: data.response }]);
    } catch (error) {
      console.error('Error:', error);
      setMessages([{ 
        role: 'assistant', 
        text: 'Hi! 👋 How can I help you with your hairstyle today?' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMessage = inputMessage.trim();
    setMessages(prev => [...prev, { role: 'user', text: userMessage }]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch('https://ai-hairstyle-studio-1-9ldn.onrender.com/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          user_id: userId.current,
          message: userMessage,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setMessages(prev => [...prev, { role: 'assistant', text: data.response }]);
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        text: 'Sorry, I encountered an error. Please try again later.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const renderMessage = (message) => {
    return (
      <div className="prose prose-sm max-w-none">
        <ReactMarkdown
          components={{
            p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
            ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-1">{children}</ul>,
            li: ({ children }) => <li className="text-sm">{children}</li>,
            strong: ({ children }) => <strong className="font-semibold text-teal-900">{children}</strong>,
            em: ({ children }) => <em className="text-teal-800 font-medium not-italic">{children}</em>,
            a: ({ href, children }) => (
            <a 
              href={href} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-teal-600 hover:underline"
            >
              {children}
            </a>
          ),
          }}
        >
          {message}
        </ReactMarkdown>
      </div>
    );
  };



  return (
    <div className="fixed bottom-6 right-6 z-[9999]">
      <AnimatePresence>
        {/* Chat Button */}
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          className="bg-teal-500 hover:bg-teal-600 text-white rounded-full p-4 shadow-lg transition-all duration-200 ease-in-out group relative"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {isOpen ? (
            <IoMdClose className="w-6 h-6" />
          ) : (
            <>
              <IoChatbubbleEllipsesOutline className="w-6 h-6" />
              {!hasInitialized && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center"
                >
                  1
                </motion.span>
              )}
            </>
          )}
          {!isOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="absolute hidden group-hover:block right-full mr-3 bg-black text-white px-3 py-1 rounded text-sm whitespace-nowrap"
            >
              Chat with AI Stylist
            </motion.div>
          )}
        </motion.button>

        {/* Chat Window */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-20 right-0 w-[400px] h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-100"
            style={{
              maxHeight: 'calc(100vh - 120px)',
              bottom: 'calc(2rem + 48px)'
            }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white p-6 relative overflow-hidden">
              <div className="relative z-10">
                <h3 className="text-xl font-semibold mb-1 text-white">AI Hairstyle Assistant</h3>
                <p className="text-sm text-teal-50 opacity-90 flex items-center">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                  Your personal style advisor 💇‍♀️
                </p>
              </div>
              <div className="absolute top-0 right-0 w-40 h-40 bg-teal-400 rounded-full filter blur-3xl opacity-50 transform translate-x-20 -translate-y-10"></div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6 bg-gradient-to-b from-gray-50 to-white">
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex ${
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div
                    className={`max-w-[90%] rounded-2xl p-4 ${
                      message.role === 'user'
                        ? 'bg-teal-500 text-white shadow-lg'
                        : 'bg-white text-teal-900 shadow-md border border-teal-100'
                    }`}
                    style={{
                      boxShadow: message.role === 'user' 
                        ? '0 4px 6px -1px rgba(45, 212, 191, 0.1), 0 2px 4px -1px rgba(45, 212, 191, 0.06)'
                        : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    }}
                  >
                    {message.role === 'user' ? (
                      <p className="text-sm font-medium">{message.text}</p>
                    ) : (
                      <div className="text-teal-900">
                        {renderMessage(message.text)}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start"
                >
                  <div className="bg-white rounded-2xl p-4 shadow-md border border-teal-100">
                    <div className="flex items-center space-x-3">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                      </div>
                      <span className="text-sm text-teal-600">Thinking of the perfect style for you...</span>
                    </div>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Form */}
            <motion.form
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              onSubmit={handleSubmit}
              className="p-4 bg-white border-t border-teal-100"
            >
              <div className="flex space-x-2 items-center">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Tell me about your dream hairstyle..."
                  className="flex-1 border border-teal-200 rounded-full px-6 py-3 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition-all text-sm text-teal-900 placeholder-teal-400"
                />
                <motion.button
                  type="submit"
                  disabled={isLoading}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-teal-500 text-white p-3 rounded-full hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                >
                  <RiSendPlaneFill className="w-5 h-5" />
                </motion.button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChatWidget;
